import React, { useEffect, useState } from 'react';
import './SeoEnricherSections.css';
import {
  SeoEnricherFaq,
  SeoEnricherSection
} from '../../utils/seoEnricherPreview';
import { isSeoEnricherIntent } from '../../constants/seoEnricherIntents';
import { getAppEnv } from '../../../config/env';

import {
  CIRCLE_INFO_ICON_PATH,
  getSeoEnricherIntentIconPath
} from './intentIconPaths';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

const SectionFaqs = ({
  faqs,
  heading
}: {
  faqs: SeoEnricherFaq[];
  heading: string;
}) => {
  if (!faqs.length) return null;

  return (
    <div className="inclExcl__faqs">
      <h3 className="inclExcl__faqSubtitle latoBold">{heading}</h3>
      <div className="inclExcl__faqList">
        {faqs.map((faq, faqIdx) => (
          <div key={`faq-${faqIdx}`} className="inclExcl__faqItem">
            <p className="inclExcl__faqQuestion">{faq.question}</p>
            <div
              className="inclExcl__faqAnswer"
              dangerouslySetInnerHTML={{ __html: faq.answer }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

type SeoEnricherSectionsProps = {
  sourceCityName?: string;
  destinationCityName?: string;
  /**
   * Pre-resolved sections (token replacement / source selection happens in
   * the parent, e.g. RouteTemplate).
   */
  sections?: SeoEnricherSection[];
};

const SeoEnricherSections = ({
  sourceCityName,
  destinationCityName,
  sections
}: SeoEnricherSectionsProps) => {
  const previewSections = sections && sections.length ? sections : [];

  // On SSR (and the first hydration render) isClient is false, so all content
  // is rendered inline & visible in the HTML. After mount we only hide it with
  // CSS (it stays in the DOM for SEO); users open it via modal on click.
  const [isClient, setIsClient] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!previewSections.length) return null;

  const title =
    sourceCityName && destinationCityName
      ? `${sourceCityName} to ${destinationCityName} Cab Booking Guide on MakeMyTrip`
      : 'Cab Booking Guide on MakeMyTrip';

  const activeSection =
    activeIdx !== null ? previewSections[activeIdx] : undefined;

  const openSection = (idx: number) => setActiveIdx(idx);

  const handleItemKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    idx: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openSection(idx);
    }
  };

  return (
    <section className="inclExcl">
      <div className="inclExcl__header">
        <h2 className="inclExcl__title latoBold">{title}</h2>
      </div>

      <div className="inclExcl__grid">
        {previewSections.map((section, idx) => {
          const resolvedIntent =
            section.intent && isSeoEnricherIntent(section.intent)
              ? section.intent
              : null;
          const intentIconPath = getSeoEnricherIntentIconPath(resolvedIntent);
          const intentIconUrl = `${PUBLIC_URL}/static/public${intentIconPath}`;
          const isClickable = isClient && Boolean(section.content);
          return (
            <div
              className={`inclExcl__item${
                isClickable ? ' inclExcl__item--clickable' : ''
              }`}
              key={`item-${idx}`}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              aria-label={
                isClickable ? `More about ${section.title}` : undefined
              }
              onClick={isClickable ? () => openSection(idx) : undefined}
              onKeyDown={
                isClickable ? event => handleItemKeyDown(event, idx) : undefined
              }
            >
              <div className="inclExcl__itemMain">
                <span className="inclExcl__intentIcon" aria-hidden="true">
                  <img
                    className="inclExcl__intentImage"
                    src={intentIconUrl}
                    alt=""
                  />
                </span>
                <div className="inclExcl__itemBody">
                  <h3 className="inclExcl__itemTitle latoBold">
                    {section.title}
                  </h3>
                  <p className="inclExcl__itemSubtitle latoRegular">
                    {section.subtitle}
                  </p>
                </div>
                <span className="inclExcl__infoBtn" aria-hidden="true">
                  <img
                    className="inclExcl__infoImage"
                    src={`${PUBLIC_URL}/static/public${CIRCLE_INFO_ICON_PATH}`}
                    alt=""
                  />
                </span>
              </div>
              {/* Always in the HTML for SEO. Hidden visually after hydration. */}
              {section.content ? (
                <div
                  className={`inclExcl__infoBox${
                    isClient ? ' inclExcl__infoBox--hidden' : ''
                  }`}
                  aria-hidden={isClient || undefined}
                >
                  <div dangerouslySetInnerHTML={{ __html: section.content }} />
                  <SectionFaqs
                    faqs={section.faqs}
                    heading={`FAQs — ${section.title}`}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Client-only: clicking a section card opens the content in a modal. */}
      {isClient && activeSection && activeSection.content ? (
        <div
          className="inclExcl__modalOverlay"
          onClick={() => setActiveIdx(null)}
        >
          <div
            className="inclExcl__modal"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <div className="inclExcl__modalHeader">
              <div>
                <h3 className="inclExcl__modalTitle latoBold">
                  {activeSection.title}
                </h3>
                {activeSection.subtitle ? (
                  <p className="inclExcl__modalSubtitle latoRegular">
                    {activeSection.subtitle}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="inclExcl__modalClose"
                aria-label="Close"
                onClick={() => setActiveIdx(null)}
              >
                ×
              </button>
            </div>
            <div className="inclExcl__modalBody">
              <div
                dangerouslySetInnerHTML={{ __html: activeSection.content }}
              />
              <SectionFaqs
                faqs={activeSection.faqs}
                heading={`FAQs — ${activeSection.title}`}
              />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SeoEnricherSections;
